import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Copy, Server, Eye, EyeOff } from "lucide-react";

export async function copyToClipboard(text, label) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text || "");
    } else {
      const ta = document.createElement("textarea");
      ta.value = text || "";
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    toast.success(`${label} copied`);
  } catch {
    toast.error("Copy failed — select and copy manually");
  }
}

export function ObsIngestDialog({ open, dest, onClose }) {
  const [showKey, setShowKey] = useState(false);
  useEffect(() => { if (open) setShowKey(false); }, [open, dest?.id]);
  if (!dest) return null;
  const isSrt = dest.protocol === "srt";
  const server = dest.endpoint || "";
  const key = dest.stream_key || "";
  const Row = ({ label, value, masked, testid }) => (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</Label>
      <div className="mt-1 flex items-center gap-1">
        <Input
          readOnly
          value={masked && !showKey ? "•".repeat(Math.min((value || "").length, 24)) : value}
          className="rounded-sm font-mono text-xs bg-background"
          data-testid={`obs-${testid}-value`}
          onFocus={(e) => e.target.select()}
        />
        {masked && (
          <Button variant="outline" size="sm" className="rounded-sm h-9 px-2" onClick={() => setShowKey((v) => !v)} data-testid={`obs-${testid}-reveal`} title={showKey ? "Hide" : "Reveal"}>
            {showKey ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </Button>
        )}
        <Button variant="outline" size="sm" className="rounded-sm h-9 px-2 border-sky-500/40 text-sky-400" onClick={() => copyToClipboard(value, label)} data-testid={`obs-${testid}-copy`} title={`Copy ${label}`}>
          <Copy className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border rounded-sm max-w-lg" data-testid="obs-ingest-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Server className="w-4 h-4" /> Encoder / OBS ingest — {dest.name}</DialogTitle>
          <DialogDescription className="text-xs">
            {isSrt
              ? "Point your SRT encoder (OBS: Settings → Stream → Service: Custom, Server = the SRT URL below)."
              : "In OBS: Settings → Stream → Service: Custom. Paste the Server and Stream Key below."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Row label={isSrt ? "SRT URL" : "Server (URL)"} value={server} testid="server" />
          {!isSrt && <Row label="Stream Key" value={key} masked testid="key" />}
          <div className="rounded-sm border border-sky-500/25 bg-sky-500/5 px-3 py-2 text-[11px] font-mono text-sky-300/80 leading-snug">
            Protocol: <span className="text-sky-200">{(dest.protocol || "").toUpperCase()}</span>
            {" · "}Recommended encode: <span className="text-sky-200">{["rtmp", "rtmps"].includes(dest.protocol) ? (dest.encode_preset && dest.encode_preset !== "copy" ? dest.encode_preset : "720p25 (H.264, 25fps)") : (dest.encode_preset || "copy")}</span>
            {" · "}Audio: <span className="text-sky-200">AAC {dest.audio_bitrate_kbps || 128}k, 44.1kHz stereo</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose} data-testid="obs-ingest-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
